import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Box,
    Radio,
    CircularProgress,
} from '@mui/material';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import DownloadUtils from '../../../../utils/download-utils';

function WhiteboardSelectionModal({ open, onClose, onSelect, baseContentId, hasActiveDrawings = false }) {
    const { whiteboardStore, authStore, imageStore } = useContext(StoreContext);
    const [filteredWhiteboards, setFilteredWhiteboards] = useState([]);
    const [selectedWhiteboardId, setSelectedWhiteboardId] = useState(null);
    const [imageMap, setImageMap] = useState({});
    const [loading] = useState(false);

    useEffect(() => {
        if (!open || !whiteboardStore.isInitialized) return;

        // Filter whiteboards by matching baseContentId (handoutId in whiteboard data)
        const whiteboards = Object.values(whiteboardStore.whiteboardData);
        const matching = whiteboards.filter((wb) => wb.handoutId === baseContentId && wb.whiteboardStatus === 'active');

        console.log('[WB Selection] Base content ID:', baseContentId);
        console.log('[WB Selection] Total whiteboards:', whiteboards.length);
        console.log('[WB Selection] Matching whiteboards:', matching.length);

        setFilteredWhiteboards(matching);
    }, [open, whiteboardStore.whiteboardData, whiteboardStore.isInitialized, baseContentId]);

    // Fetch images for whiteboards
    useEffect(() => {
        if (filteredWhiteboards.length === 0) return;

        const fetchImages = async () => {
            for (const wb of filteredWhiteboards) {
                if (!wb.canvasImageLink) continue;

                try {
                    // Check if already loaded
                    if (imageMap[wb.canvasImageLink]) continue;

                    // Check IndexedDB first
                    const cached = await imageStore.getImageFromIndexedDB(wb.canvasImageLink);
                    if (cached) {
                        setImageMap((prev) => ({ ...prev, [wb.canvasImageLink]: cached.data }));
                        continue;
                    }

                    // Generate presigned URL
                    const payload = {
                        files: [{ method: 'get', key: wb.canvasImageLink }],
                        contentCategory: 'whiteboard',
                    };
                    const response = await UserApi.generatePresignedUrls(authStore.idToken, payload);
                    if (response.getUrls?.[0]?.presignedUrl) {
                        const presignedUrl = response.getUrls[0].presignedUrl;

                        // Download and convert to base64 using DownloadUtils
                        const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(presignedUrl);

                        // Store in IndexedDB
                        await imageStore.saveImageToIndexedDB(
                            wb.canvasImageLink,
                            downloadResult.base64data,
                            downloadResult.eTag,
                        );

                        // Update state
                        setImageMap((prev) => ({ ...prev, [wb.canvasImageLink]: downloadResult.base64data }));
                    }
                } catch (error) {
                    console.error(`[WB Selection] Failed to fetch image for ${wb.whiteboardId}:`, error);
                }
            }
        };

        fetchImages();
    }, [filteredWhiteboards, authStore.idToken, imageStore, imageMap]);

    const handleSelect = () => {
        const selected = filteredWhiteboards.find((wb) => wb.whiteboardId === selectedWhiteboardId);
        if (selected) {
            onSelect(selected);
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select Whiteboard Illustration</DialogTitle>
            <DialogContent>
                {hasActiveDrawings && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                        Your whiteboard drawings will be kept on the new illustration.
                    </Typography>
                )}
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : filteredWhiteboards.length === 0 ? (
                    <Box p={4} textAlign="center">
                        <Typography color="textSecondary">
                            You don&apos;t have any saved illustrations yet. To create one, select a handout or diagram
                            and start a &apos;customise&apos; session. Use the whiteboard tools to annotate your chosen
                            illustration, then click &apos;save&apos; to store it.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {filteredWhiteboards.map((wb) => (
                            <Grid item xs={12} sm={6} md={4} key={wb.whiteboardId}>
                                <Card
                                    sx={{
                                        cursor: 'pointer',
                                        border:
                                            selectedWhiteboardId === wb.whiteboardId
                                                ? '2px solid #FF669B'
                                                : '2px solid transparent',
                                        position: 'relative',
                                    }}
                                    onClick={() => setSelectedWhiteboardId(wb.whiteboardId)}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        {imageMap[wb.canvasImageLink] ? (
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={imageMap[wb.canvasImageLink]}
                                                alt={wb.handoutDocumentTitle}
                                                sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: 200,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: '#f5f5f5',
                                                }}
                                            >
                                                <CircularProgress size={24} />
                                            </Box>
                                        )}
                                        <Radio
                                            checked={selectedWhiteboardId === wb.whiteboardId}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'white',
                                            }}
                                        />
                                    </Box>
                                    <CardContent>
                                        <Typography variant="body2" noWrap>
                                            {wb.handoutDocumentTitle}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Updated: {new Date(wb.lastUpdated).toLocaleDateString()}
                                        </Typography>
                                        {wb.notes && (
                                            <Typography variant="caption" display="block" color="textSecondary" noWrap>
                                                {wb.notes}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSelect}
                    variant="contained"
                    disabled={!selectedWhiteboardId}
                    sx={{
                        bgcolor: '#FF669B',
                        '&:hover': { bgcolor: '#FF4D88' },
                    }}
                >
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default WhiteboardSelectionModal;
